import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { format, startOfMonth, endOfMonth, startOfWeek, endOfWeek, addDays, addMonths, subMonths, isSameMonth, isSameDay, parseISO } from "date-fns";
import { pt } from "date-fns/locale";
import { ChevronLeft, ChevronRight, Plus, X, Waves, BookMarked, Trash2, Edit, Loader2, Calendar as CalendarIcon } from "lucide-react";
import { Card, CardContent } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { API } from "../App";
import { BookingFormDialog, LessonFormDialog, LessonDetailModal } from "../components/calendar";

const WEEKDAYS = ["Seg", "Ter", "Qua", "Qui", "Sex", "Sáb", "Dom"];

const CalendarPage = () => {
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [lessons, setLessons] = useState([]);
  const [bookings, setBookings] = useState([]);
  const [selectedDate, setSelectedDate] = useState(null);
  const [loading, setLoading] = useState(true);

  // Booking form
  const [showBookingForm, setShowBookingForm] = useState(false);
  const [editingBooking, setEditingBooking] = useState(null);
  const [bookingForm, setBookingForm] = useState({ time_slot: "", notes: "" });

  // Lesson creation
  const [showLessonForm, setShowLessonForm] = useState(false);
  const [lessonForm, setLessonForm] = useState({
    lesson_category: "surf", lesson_type: "group", start_time: "10:00", end_time: "12:00", max_students: 8, price: 25,
  });
  const [lessonStudentSearch, setLessonStudentSearch] = useState("");
  const [lessonStudentResults, setLessonStudentResults] = useState([]);
  const [lessonSelectedStudents, setLessonSelectedStudents] = useState([]);
  const [showInlineStudentCreate, setShowInlineStudentCreate] = useState(false);
  const [inlineStudent, setInlineStudent] = useState({ name: "", email: "", phone: "" });

  // Google Calendar
  const [gcalStatus, setGcalStatus] = useState({ connected: false, email: "" });
  const [gcalSyncing, setGcalSyncing] = useState(false);

  // Lesson detail modal
  const [showLessonDetail, setShowLessonDetail] = useState(false);
  const [selectedLesson, setSelectedLesson] = useState(null);
  const [lessonEnrollments, setLessonEnrollments] = useState([]);
  const [loadingEnrollments, setLoadingEnrollments] = useState(false);
  // Add student to lesson
  const [addStudentSearch, setAddStudentSearch] = useState("");
  const [addStudentResults, setAddStudentResults] = useState([]);
  const [addStudentLoading, setAddStudentLoading] = useState(false);
  const [showAddStudentPanel, setShowAddStudentPanel] = useState(false);
  // Inline student creation in lesson detail
  const [showDetailStudentCreate, setShowDetailStudentCreate] = useState(false);
  const [detailNewStudent, setDetailNewStudent] = useState({ name: "", email: "", phone: "" });

  useEffect(() => {
    fetchCalendarData();
    fetchGcalStatus();
  }, [currentMonth]);

  const fetchCalendarData = useCallback(async () => {
    try {
      const m = String(currentMonth.getMonth() + 1);
      const y = String(currentMonth.getFullYear());
      const res = await axios.get(`${API}/bookings/calendar?month=${m}&year=${y}`, { withCredentials: true });
      setLessons(res.data.lessons || []);
      setBookings(res.data.bookings || []);
    } catch (error) {
      console.error("Error fetching calendar:", error);
    } finally {
      setLoading(false);
    }
  }, [currentMonth]);

  const fetchGcalStatus = async () => {
    try {
      const res = await axios.get(`${API}/oauth/calendar/status`, { withCredentials: true });
      setGcalStatus(res.data);
    } catch {}
  };

  const handleGcalConnect = async () => {
    try {
      const res = await axios.get(`${API}/oauth/calendar/connect`, { withCredentials: true });
      window.location.href = res.data.authorization_url;
    } catch (e) { toast.error(e.response?.data?.detail || "Erro ao conectar Google Calendar"); }
  };

  const handleGcalDisconnect = async () => {
    if (!window.confirm("Desconectar Google Calendar?")) return;
    try {
      await axios.post(`${API}/oauth/calendar/disconnect`, {}, { withCredentials: true });
      setGcalStatus({ connected: false, email: "" });
      toast.success("Google Calendar desconectado");
    } catch (e) { toast.error("Erro"); }
  };

  const handleGcalSync = async () => {
    setGcalSyncing(true);
    try {
      const m = currentMonth.getMonth() + 1;
      const y = currentMonth.getFullYear();
      const res = await axios.post(`${API}/oauth/calendar/sync`, { month: m, year: y }, { withCredentials: true });
      toast.success(res.data.message);
      if (res.data.errors?.length > 0) {
        console.warn("Sync errors:", res.data.errors);
      }
    } catch (e) { toast.error(e.response?.data?.detail || "Erro ao exportar"); }
    finally { setGcalSyncing(false); }
  };

  // Check for gcal callback params
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get("gcal_connected")) {
      toast.success("Google Calendar conectado com sucesso!");
      fetchGcalStatus();
      window.history.replaceState({}, "", window.location.pathname);
    }
    if (params.get("gcal_error")) {
      toast.error(`Erro Google Calendar: ${params.get("gcal_error")}`);
      window.history.replaceState({}, "", window.location.pathname);
    }
  }, []);

  const handleCreateBooking = async () => {
    if (!bookingForm.notes.trim()) { toast.error("Adiciona uma nota"); return; }
    try {
      if (editingBooking) {
        await axios.put(`${API}/bookings/${editingBooking.booking_id}`, { ...bookingForm, date: selectedDate }, { withCredentials: true });
        toast.success("Marcação atualizada");
      } else {
        await axios.post(`${API}/bookings`, { date: selectedDate, ...bookingForm }, { withCredentials: true });
        toast.success("Marcação criada");
      }
      setShowBookingForm(false);
      setEditingBooking(null);
      setBookingForm({ time_slot: "", notes: "" });
      fetchCalendarData();
    } catch (e) { toast.error(e.response?.data?.detail || "Erro"); }
  };

  const handleDeleteBooking = async (bookingId) => {
    if (!window.confirm("Eliminar esta marcação?")) return;
    try {
      await axios.delete(`${API}/bookings/${bookingId}`, { withCredentials: true });
      toast.success("Marcação eliminada");
      fetchCalendarData();
    } catch (e) { toast.error(e.response?.data?.detail || "Erro"); }
  };

  // Lesson student search
  useEffect(() => {
    if (!showLessonForm) return;
    const timer = setTimeout(async () => {
      if (!lessonStudentSearch.trim()) { setLessonStudentResults([]); return; }
      try {
        const res = await axios.get(`${API}/admin/students?search=${encodeURIComponent(lessonStudentSearch)}&limit=6`, { withCredentials: true });
        const students = res.data.students || res.data || [];
        const selectedIds = new Set(lessonSelectedStudents.map(s => s.user_id));
        setLessonStudentResults(students.filter(s => !selectedIds.has(s.user_id)));
      } catch { setLessonStudentResults([]); }
    }, 300);
    return () => clearTimeout(timer);
  }, [lessonStudentSearch, showLessonForm, lessonSelectedStudents]);

  const openNewLesson = (dateStr) => {
    setSelectedDate(dateStr);
    setLessonForm({ lesson_category: "surf", lesson_type: "group", start_time: "10:00", end_time: "12:00", max_students: 8, price: 25 });
    setLessonSelectedStudents([]);
    setLessonStudentSearch("");
    setLessonStudentResults([]);
    setShowInlineStudentCreate(false);
    setShowLessonForm(true);
  };

  const handleCreateLesson = async () => {
    if (!selectedDate) return;
    try {
      const payload = { ...lessonForm, date: selectedDate, time_slot: `${lessonForm.start_time}-${lessonForm.end_time}` };
      const res = await axios.post(`${API}/lessons`, payload, { withCredentials: true });
      const lessonId = res.data.lesson_id;
      // Enroll selected students
      let enrolled = 0;
      for (const student of lessonSelectedStudents) {
        try {
          await axios.post(`${API}/staff/enrollments`, { lesson_id: lessonId, user_id: student.user_id }, { withCredentials: true });
          enrolled++;
        } catch {}
      }
      const msg = enrolled > 0 ? `Aula criada com ${enrolled} aluno${enrolled > 1 ? "s" : ""} inscrito${enrolled > 1 ? "s" : ""}` : "Aula criada";
      toast.success(msg);
      setShowLessonForm(false);
      fetchCalendarData();
    } catch (e) { toast.error(e.response?.data?.detail || "Erro ao criar aula"); }
  };

  const handleInlineCreateStudent = async () => {
    if (!inlineStudent.name.trim()) { toast.error("Nome é obrigatório"); return; }
    try {
      const res = await axios.post(`${API}/admin/students`, inlineStudent, { withCredentials: true });
      setLessonSelectedStudents(prev => [...prev, res.data]);
      setInlineStudent({ name: "", email: "", phone: "" });
      setShowInlineStudentCreate(false);
      toast.success(`Aluno "${res.data.name}" criado e adicionado`);
    } catch (e) { toast.error(e.response?.data?.detail || "Erro ao criar aluno"); }
  };

  // Open lesson detail modal
  const openLessonDetail = async (lesson) => {
    setSelectedLesson(lesson);
    setShowLessonDetail(true);
    setLessonEnrollments([]);
    setLoadingEnrollments(true);
    setShowAddStudentPanel(false);
    setAddStudentSearch("");
    setAddStudentResults([]);
    setShowDetailStudentCreate(false);
    setDetailNewStudent({ name: "", email: "", phone: "" });
    try {
      const res = await axios.get(`${API}/enrollments/lesson/${lesson.lesson_id}`, { withCredentials: true });
      setLessonEnrollments(res.data);
    } catch { toast.error("Erro ao carregar inscrições"); }
    finally { setLoadingEnrollments(false); }
  };

  // Refresh lesson enrollments
  const refreshLessonEnrollments = async () => {
    if (!selectedLesson) return;
    try {
      const res = await axios.get(`${API}/enrollments/lesson/${selectedLesson.lesson_id}`, { withCredentials: true });
      setLessonEnrollments(res.data);
    } catch {}
  };

  // Cancel lesson
  const handleCancelLesson = async () => {
    if (!selectedLesson) return;
    if (!window.confirm("Cancelar esta aula? Todos os créditos serão devolvidos aos alunos inscritos.")) return;
    try {
      const res = await axios.post(`${API}/lessons/${selectedLesson.lesson_id}/cancel`, {}, { withCredentials: true });
      toast.success(res.data.message);
      setShowLessonDetail(false);
      setSelectedLesson(null);
      fetchCalendarData();
    } catch (e) { toast.error(e.response?.data?.detail || "Erro"); }
  };

  // Mark attendance
  const handleMarkAttendance = async (enrollmentId) => {
    try {
      await axios.patch(`${API}/enrollments/${enrollmentId}`, { status: "attended" }, { withCredentials: true });
      toast.success("Presença confirmada");
      refreshLessonEnrollments();
    } catch (e) { toast.error(e.response?.data?.detail || "Erro ao marcar presença"); }
  };

  // Mark absent
  const handleMarkAbsent = async (enrollmentId, refund) => {
    try {
      await axios.post(`${API}/enrollments/${enrollmentId}/absent`, { refund }, { withCredentials: true });
      toast.success(refund ? "Falta marcada (crédito devolvido)" : "Falta marcada");
      refreshLessonEnrollments();
    } catch (e) { toast.error(e.response?.data?.detail || "Erro"); }
  };

  // Revert attendance
  const handleRevertAttendance = async (enrollmentId) => {
    if (!window.confirm("Reverter a presença deste aluno? O crédito será devolvido se aplicável.")) return;
    try {
      const res = await axios.patch(`${API}/enrollments/${enrollmentId}/revert-attendance`, {}, { withCredentials: true });
      toast.success(res.data.message);
      refreshLessonEnrollments();
    } catch (e) { toast.error(e.response?.data?.detail || "Erro ao reverter presença"); }
  };

  // Search students for add to lesson
  useEffect(() => {
    if (!showAddStudentPanel) return;
    const timer = setTimeout(async () => {
      if (!addStudentSearch.trim()) { setAddStudentResults([]); return; }
      try {
        const res = await axios.get(`${API}/admin/students?search=${encodeURIComponent(addStudentSearch)}&limit=8`, { withCredentials: true });
        const students = res.data.students || res.data || [];
        const enrolledIds = new Set(lessonEnrollments.map(e => e.user_id));
        setAddStudentResults(students.filter(s => !enrolledIds.has(s.user_id)));
      } catch { setAddStudentResults([]); }
    }, 300);
    return () => clearTimeout(timer);
  }, [addStudentSearch, showAddStudentPanel, lessonEnrollments]);

  // Add student to lesson
  const handleAddStudentToLesson = async (studentId) => {
    if (!selectedLesson) return;
    setAddStudentLoading(true);
    try {
      const res = await axios.post(`${API}/staff/enrollments`, { lesson_id: selectedLesson.lesson_id, user_id: studentId }, { withCredentials: true });
      toast.success(res.data.message);
      refreshLessonEnrollments();
      fetchCalendarData();
      setAddStudentSearch("");
      setAddStudentResults([]);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erro ao adicionar aluno");
    } finally { setAddStudentLoading(false); }
  };

  // Create new student and add to lesson
  const handleCreateAndAddStudent = async () => {
    if (!detailNewStudent.name.trim()) { toast.error("Nome é obrigatório"); return; }
    if (!selectedLesson) return;
    setAddStudentLoading(true);
    try {
      // Create student
      const createRes = await axios.post(`${API}/admin/students`, detailNewStudent, { withCredentials: true });
      const newStudentId = createRes.data.user_id;
      // Add to lesson
      const enrollRes = await axios.post(`${API}/staff/enrollments`, { lesson_id: selectedLesson.lesson_id, user_id: newStudentId }, { withCredentials: true });
      toast.success(`Aluno "${detailNewStudent.name}" criado e adicionado à aula`);
      refreshLessonEnrollments();
      fetchCalendarData();
      setDetailNewStudent({ name: "", email: "", phone: "" });
      setShowDetailStudentCreate(false);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erro ao criar aluno");
    } finally { setAddStudentLoading(false); }
  };

  const openNewBooking = (dateStr) => {
    setSelectedDate(dateStr);
    setEditingBooking(null);
    setBookingForm({ time_slot: "", notes: "" });
    setShowBookingForm(true);
  };

  const openEditBooking = (booking) => {
    setSelectedDate(booking.date);
    setEditingBooking(booking);
    setBookingForm({ time_slot: booking.time_slot || "", notes: booking.notes || "" });
    setShowBookingForm(true);
  };

  // Build calendar grid
  const monthStart = startOfMonth(currentMonth);
  const monthEnd = endOfMonth(currentMonth);
  const calStart = startOfWeek(monthStart, { weekStartsOn: 1 });
  const calEnd = endOfWeek(monthEnd, { weekStartsOn: 1 });

  const days = [];
  let day = calStart;
  while (day <= calEnd) {
    days.push(day);
    day = addDays(day, 1);
  }

  // Group data by date
  const lessonsByDate = {};
  for (const l of lessons) {
    if (!lessonsByDate[l.date]) lessonsByDate[l.date] = [];
    lessonsByDate[l.date].push(l);
  }
  const bookingsByDate = {};
  for (const b of bookings) {
    if (!bookingsByDate[b.date]) bookingsByDate[b.date] = [];
    bookingsByDate[b.date].push(b);
  }

  const today = format(new Date(), "yyyy-MM-dd");
  const selectedDateLessons = selectedDate ? (lessonsByDate[selectedDate] || []) : [];
  const selectedDateBookings = selectedDate ? (bookingsByDate[selectedDate] || []) : [];

  return (
    <div className="space-y-4 sm:space-y-6">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div>
          <h1 className="text-xl md:text-2xl font-bold text-[#3d5a63]" data-testid="calendar-title">Calendário</h1>
          <p className="text-sm text-[#475569]">Aulas, marcações e planeamento</p>
        </div>
        <div className="flex gap-2 flex-wrap">
          {gcalStatus.connected ? (
            <>
              <Button
                variant="outline"
                size="sm"
                onClick={handleGcalSync}
                disabled={gcalSyncing}
                data-testid="gcal-sync-btn"
                className="text-[#6697a4] border-[#6697a4]/30"
              >
                {gcalSyncing ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <CalendarIcon className="h-4 w-4 mr-1" />}
                {gcalSyncing ? "A exportar..." : "Exportar para Google"}
              </Button>
              <Button variant="ghost" size="sm" onClick={handleGcalDisconnect} className="text-xs text-[#94A3B8]" data-testid="gcal-disconnect-btn">
                {gcalStatus.email} (desconectar)
              </Button>
            </>
          ) : (
            <Button variant="outline" size="sm" onClick={handleGcalConnect} data-testid="gcal-connect-btn" className="text-[#6697a4] border-[#6697a4]/30">
              <CalendarIcon className="h-4 w-4 mr-1" /> Conectar Google Calendar
            </Button>
          )}
        </div>
      </div>

      {/* Month Navigation */}
      <div className="flex items-center justify-between">
        <Button variant="ghost" size="sm" onClick={() => setCurrentMonth(subMonths(currentMonth, 1))} data-testid="prev-month">
          <ChevronLeft className="h-5 w-5" />
        </Button>
        <h2 className="text-lg font-semibold text-[#3d5a63] capitalize" data-testid="current-month">
          {format(currentMonth, "MMMM yyyy", { locale: pt })}
        </h2>
        <Button variant="ghost" size="sm" onClick={() => setCurrentMonth(addMonths(currentMonth, 1))} data-testid="next-month">
          <ChevronRight className="h-5 w-5" />
        </Button>
      </div>

      {/* Calendar Grid */}
      <Card className="bg-white border-0 shadow-sm rounded-xl overflow-hidden">
        <CardContent className="p-0">
          {/* Weekday headers */}
          <div className="grid grid-cols-7 border-b border-slate-100">
            {WEEKDAYS.map((d) => (
              <div key={d} className="py-2 text-center text-xs font-medium text-[#94A3B8] uppercase tracking-wide">
                {d}
              </div>
            ))}
          </div>
          {/* Day cells */}
          <div className="grid grid-cols-7" data-testid="calendar-grid">
            {days.map((d, idx) => {
              const dateStr = format(d, "yyyy-MM-dd");
              const isCurrentMonth = isSameMonth(d, currentMonth);
              const isToday = dateStr === today;
              const isSelected = dateStr === selectedDate;
              const dayLessons = lessonsByDate[dateStr] || [];
              const dayBookings = bookingsByDate[dateStr] || [];
              const hasEvents = dayLessons.length > 0 || dayBookings.length > 0;

              return (
                <button
                  key={dateStr}
                  type="button"
                  onClick={() => setSelectedDate(isSelected ? null : dateStr)}
                  className={`min-h-[72px] sm:min-h-[90px] p-1 sm:p-1.5 border-b border-r border-slate-50 text-left transition-colors relative
                    ${!isCurrentMonth ? "bg-slate-50/50 opacity-40" : ""}
                    ${isSelected ? "bg-[#6697a4]/5 ring-2 ring-[#6697a4]/30 ring-inset" : "hover:bg-slate-50"}
                  `}
                  data-testid={`calendar-day-${dateStr}`}
                >
                  <span className={`text-xs sm:text-sm font-medium inline-flex items-center justify-center w-6 h-6 rounded-full
                    ${isToday ? "bg-[#6697a4] text-white" : isCurrentMonth ? "text-[#0F172A]" : "text-[#CBD5E1]"}
                  `}>
                    {format(d, "d")}
                  </span>
                  {/* Event indicators */}
                  <div className="mt-0.5 space-y-0.5 overflow-hidden">
                    {dayLessons.slice(0, 2).map((l) => (
                      <div key={l.lesson_id} className="text-[9px] sm:text-[10px] leading-tight truncate px-1 py-0.5 rounded bg-[#6697a4]/10 text-[#3d5a63]">
                        <Waves className="h-2.5 w-2.5 inline mr-0.5 text-[#6697a4]" />
                        {l.start_time} {l.enrolled_count}/{l.max_students}
                      </div>
                    ))}
                    {dayLessons.length > 2 && (
                      <span className="text-[9px] text-[#94A3B8] pl-1">+{dayLessons.length - 2} aulas</span>
                    )}
                    {dayBookings.slice(0, 2).map((b) => (
                      <div key={b.booking_id} className="text-[9px] sm:text-[10px] leading-tight truncate px-1 py-0.5 rounded bg-amber-50 text-amber-700">
                        <BookMarked className="h-2.5 w-2.5 inline mr-0.5" />
                        {b.notes?.substring(0, 20)}
                      </div>
                    ))}
                    {dayBookings.length > 2 && (
                      <span className="text-[9px] text-[#94A3B8] pl-1">+{dayBookings.length - 2}</span>
                    )}
                  </div>
                </button>
              );
            })}
          </div>
        </CardContent>
      </Card>

      {/* Selected Day Details */}
      {selectedDate && (
        <Card className="bg-white border-0 shadow-sm rounded-xl" data-testid="day-details">
          <CardContent className="p-4 sm:p-5">
            <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
              <h3 className="font-semibold text-[#3d5a63]">
                {format(parseISO(selectedDate), "EEEE, d 'de' MMMM", { locale: pt })}
              </h3>
              <div className="flex gap-2">
                <Button size="sm" variant="outline" className="text-[#6697a4] border-[#6697a4]/30" onClick={() => openNewLesson(selectedDate)} data-testid="new-lesson-btn">
                  <Waves className="h-4 w-4 mr-1" /> Nova Aula
                </Button>
                <Button size="sm" onClick={() => openNewBooking(selectedDate)} data-testid="new-booking-btn">
                  <Plus className="h-4 w-4 mr-1" /> Nova Marcação
                </Button>
              </div>
            </div>

            {/* Lessons */}
            {selectedDateLessons.length > 0 && (
              <div className="mb-4">
                <p className="text-xs font-medium text-[#475569] mb-2 flex items-center gap-1">
                  <Waves className="h-3.5 w-3.5 text-[#6697a4]" /> Aulas ({selectedDateLessons.length})
                </p>
                <div className="space-y-2">
                  {selectedDateLessons.map((l) => (
                    <button
                      key={l.lesson_id}
                      onClick={() => openLessonDetail(l)}
                      className="w-full p-3 bg-[#6697a4]/5 rounded-lg border border-[#6697a4]/10 hover:bg-[#6697a4]/10 hover:border-[#6697a4]/20 transition-colors text-left group"
                      data-testid={`calendar-lesson-${l.lesson_id}`}
                    >
                      <div className="flex items-center justify-between">
                        <span className="text-sm font-medium text-[#3d5a63] group-hover:text-[#6697a4]">
                          {l.title || l.lesson_category}
                        </span>
                        <div className="flex items-center gap-2">
                          <Badge className="bg-[#6697a4]/10 text-[#6697a4] border-0 text-[10px]">
                            {l.enrolled_count}/{l.max_students} alunos
                          </Badge>
                          <ChevronRight className="h-4 w-4 text-[#94A3B8] group-hover:text-[#6697a4]" />
                        </div>
                      </div>
                      <p className="text-xs text-[#475569] mt-1">
                        {l.start_time} - {l.end_time} · {l.lesson_type === "private" ? "Privada" : "Grupo"}
                      </p>
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Bookings */}
            {selectedDateBookings.length > 0 && (
              <div className="mb-2">
                <p className="text-xs font-medium text-[#475569] mb-2 flex items-center gap-1">
                  <BookMarked className="h-3.5 w-3.5 text-amber-600" /> Marcações ({selectedDateBookings.length})
                </p>
                <div className="space-y-2">
                  {selectedDateBookings.map((b) => (
                    <div key={b.booking_id} className="p-3 bg-amber-50/50 rounded-lg border border-amber-100" data-testid={`calendar-booking-${b.booking_id}`}>
                      <div className="flex items-start justify-between gap-2">
                        <div className="min-w-0 flex-1">
                          {b.time_slot && <span className="text-xs font-medium text-amber-700 mr-2">{b.time_slot}</span>}
                          <p className="text-sm text-[#0F172A] whitespace-pre-wrap">{b.notes}</p>
                        </div>
                        <div className="flex gap-1 shrink-0">
                          <button onClick={() => openEditBooking(b)} className="p-1 rounded hover:bg-amber-100 text-amber-600" data-testid={`edit-booking-${b.booking_id}`}>
                            <Edit className="h-3.5 w-3.5" />
                          </button>
                          <button onClick={() => handleDeleteBooking(b.booking_id)} className="p-1 rounded hover:bg-red-100 text-red-500" data-testid={`delete-booking-${b.booking_id}`}>
                            <Trash2 className="h-3.5 w-3.5" />
                          </button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {selectedDateLessons.length === 0 && selectedDateBookings.length === 0 && (
              <p className="text-sm text-[#94A3B8] text-center py-4">Sem aulas ou marcações para este dia</p>
            )}
          </CardContent>
        </Card>
      )}

      {/* Booking Form Dialog */}
      <BookingFormDialog
        open={showBookingForm}
        onOpenChange={setShowBookingForm}
        selectedDate={selectedDate}
        editingBooking={editingBooking}
        bookingForm={bookingForm}
        setBookingForm={setBookingForm}
        onSave={handleCreateBooking}
      />

      {/* Create Lesson Dialog */}
      <LessonFormDialog
        open={showLessonForm}
        onOpenChange={setShowLessonForm}
        selectedDate={selectedDate}
        lessonForm={lessonForm}
        setLessonForm={setLessonForm}
        lessonStudentSearch={lessonStudentSearch}
        setLessonStudentSearch={setLessonStudentSearch}
        lessonStudentResults={lessonStudentResults}
        lessonSelectedStudents={lessonSelectedStudents}
        setLessonSelectedStudents={setLessonSelectedStudents}
        setLessonStudentResults={setLessonStudentResults}
        showInlineStudentCreate={showInlineStudentCreate}
        setShowInlineStudentCreate={setShowInlineStudentCreate}
        inlineStudent={inlineStudent}
        setInlineStudent={setInlineStudent}
        onInlineCreateStudent={handleInlineCreateStudent}
        onCreateLesson={handleCreateLesson}
      />

      {/* Lesson Detail Modal */}
      <LessonDetailModal
        open={showLessonDetail}
        onOpenChange={setShowLessonDetail}
        selectedLesson={selectedLesson}
        lessonEnrollments={lessonEnrollments}
        loadingEnrollments={loadingEnrollments}
        showAddStudentPanel={showAddStudentPanel}
        setShowAddStudentPanel={setShowAddStudentPanel}
        addStudentSearch={addStudentSearch}
        setAddStudentSearch={setAddStudentSearch}
        addStudentResults={addStudentResults}
        addStudentLoading={addStudentLoading}
        showDetailStudentCreate={showDetailStudentCreate}
        setShowDetailStudentCreate={setShowDetailStudentCreate}
        detailNewStudent={detailNewStudent}
        setDetailNewStudent={setDetailNewStudent}
        onCancelLesson={handleCancelLesson}
        onAddStudentToLesson={handleAddStudentToLesson}
        onCreateAndAddStudent={handleCreateAndAddStudent}
        onMarkAttendance={handleMarkAttendance}
        onMarkAbsent={handleMarkAbsent}
        onRevertAttendance={handleRevertAttendance}
      />
    </div>
  );
};

export default CalendarPage;
